import { makeStyles } from '@material-ui/core/styles';

/**
 * Text colours come from `theme.palette.common.*`, generated from
 * `packages/fds-tokens/colors.ts` (`syncFdsColors`), so they can't drift from
 * the design system.
 *
 * The Compliance accent has no generated FDS source — the handoff uses the
 * Compliance product's own accent, and `fds-tokens` has no entry within reach
 * of it — so it stays a named local constant, matching the pattern in the
 * stage card's `styles.ts` and `containers/OfferLetterTemplates/fdsTokens.ts`.
 */
const COMPLIANCE_ACCENT = '#f86949';

const MODAL_WIDTH = 1000;

export { COMPLIANCE_ACCENT, MODAL_WIDTH };

export const useStyles = makeStyles(theme => ({
  // MUI's DialogContent pads 24px and FDS's ModalContent caps `maxHeight` at
  // 50vh; neither fits a 490px two-column layout whose left panel is
  // full-bleed. The modal uses DialogContent directly and lifts both here, so
  // the columns paint their own insets.
  content: {
    maxHeight: 'none',
    padding: 0,
    overflow: 'hidden',
  },
  // The FDS Modal goes full-screen below `xs`. Two fixed half-width columns do
  // not fit a phone, so they stack and the preview panel takes a bounded height
  // instead of the image's natural one.
  body: {
    display: 'flex',
    alignItems: 'stretch',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  // Full-bleed to the modal's left and bottom edges: the modal clips at its
  // own rounded corners, so the panel just has to reach them.
  previewPanel: {
    flex: 1,
    // Flex items default to `min-width: auto`, so the pitch column's longest
    // unbreakable line would push the row past the modal on a narrow viewport.
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.gray100,
    padding: theme.spacing(4),
  },
  previewImage: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    [theme.breakpoints.down('xs')]: {
      maxHeight: 220,
      width: 'auto',
    },
  },
  pitch: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4),
  },
  eyebrowRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  eyebrowIcon: {
    width: 20,
    height: 20,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0.08em',
    color: theme.palette.common.gray600,
  },
  heading: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: '32px',
    color: theme.palette.common.gray800,
  },
  bodyText: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.common.gray600,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  price: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '40px',
    color: theme.palette.common.gray800,
  },
  priceUnit: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.common.gray600,
  },
  checks: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  check: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  checkIcon: {
    flexShrink: 0,
    width: 16,
    height: 16,
    color: COMPLIANCE_ACCENT,
  },
  checkText: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.common.gray600,
  },
  trialPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    width: '100%',
    backgroundColor: theme.palette.common.gray100,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
  },
  trialCopy: {
    display: 'flex',
    flexDirection: 'column',
  },
  trialCopyLine: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.common.gray800,
  },
  // Tertiary text action, not a filled button: TextButton would pull in the
  // FDS body2 metrics and an underline-on-hover treatment the handoff does not
  // ask for, so this is a plain MUI text button centred under the panel.
  discoverCompliance: {
    alignSelf: 'center',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 600,
  },
}));
